import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Shield, Eye, EyeOff, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true); setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("adminToken", res.data.access_token);
      localStorage.setItem("adminUser", JSON.stringify(res.data.user));
      navigate("/admin/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid email or password.");
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-background flex items-center justify-center px-4" style={{ minHeight: '100dvh', paddingTop: 'env(safe-area-inset-top,0px)', paddingBottom: 'env(safe-area-inset-bottom,0px)' }} data-testid="admin-login-page">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-0 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] rounded-full bg-indigo-500/6 blur-[80px] sm:blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[200px] sm:w-[300px] h-[200px] sm:h-[300px] rounded-full bg-sky-500/5 blur-[60px] sm:blur-[100px]" />
      </div>

      <div className="w-full max-w-sm sm:max-w-md relative z-10">
        <div className="text-center mb-8 sm:mb-10">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-4 sm:mb-5">
            <Shield className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="login-title">Attorney Portal</h1>
          <p className="text-muted-foreground text-sm">Sign in to access your leads dashboard</p>
        </div>

        <div className="p-6 sm:p-8 rounded-2xl border border-border/50 bg-card/30 backdrop-blur-sm">
          {error && (
            <div className="flex items-center gap-3 p-3 sm:p-4 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-sm mb-5 animate-fade-in" data-testid="login-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
            </div>
          )}
          <form onSubmit={handleLogin} className="space-y-4 sm:space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground/80">Email Address</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@icantfindalawyer.com"
                className="w-full h-11 sm:h-12 px-4 rounded-xl bg-secondary/40 border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/30 transition-all"
                data-testid="email-input" autoComplete="email" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground/80">Password</label>
              <div className="relative">
                <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                  className="w-full h-11 sm:h-12 px-4 pr-11 rounded-xl bg-secondary/40 border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                  data-testid="password-input" autoComplete="current-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1" data-testid="toggle-password">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading || !email || !password}
              className="w-full h-11 sm:h-12 rounded-full bg-primary hover:bg-primary/90 text-white font-semibold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_20px_-5px_rgba(99,102,241,0.4)] active:scale-[0.98]"
              data-testid="login-submit-btn">
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
          <div className="mt-5 pt-4 border-t border-border/30">
            <p className="text-xs text-muted-foreground/60 text-center">Authorized personnel only</p>
          </div>
        </div>

        <p className="text-center mt-5 text-sm text-muted-foreground">
          Need legal help?{" "}
          <a href="/" className="text-primary hover:text-primary/80 transition-colors">Start your intake</a>
        </p>
      </div>
    </div>
  );
}
